import { Box, Button, CircularProgress, Stack } from "@mui/material";
import type { ReactNode } from "react";
import { useLayoutEffect } from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "@/app/store";
import { STATUS } from "@/constants";
import { fetchTenantConfiguration } from "@/features/app-config/model/tenantConfigurationSlice";
import { isSharedAppPath } from "@/features/shared-apps/lib/sharedAppRoute";

type TenantConfigurationGateProps = {
  children: ReactNode;
};

/**
 * Loads `GET /v1/tenants/configuration` before the rest of the app is shown.
 *
 * Skipped on the public shared-app route: that payload is Cognito identifiers, consumed only by auth
 * code, so a logged-out viewer would otherwise be blocked behind a request it has no use for.
 * `useLocation` works here because `BrowserRouter` wraps this gate in `AppProviders`.
 */
export function TenantConfigurationGate({ children }: TenantConfigurationGateProps) {
  const { t } = useTranslation("common");
  const dispatch = useAppDispatch();
  const { status } = useAppSelector((s) => s.tenantConfiguration);
  const isPublicSharedRoute = isSharedAppPath(useLocation().pathname);

  useLayoutEffect(() => {
    if (isPublicSharedRoute) {
      return;
    }
    void dispatch(fetchTenantConfiguration());
  }, [dispatch, isPublicSharedRoute]);

  // Keyed on the effect dependency rather than a one-shot flag, so navigating from a shared link to
  // an authenticated route still loads the config instead of staying stuck unconfigured.
  if (isPublicSharedRoute) {
    return children;
  }

  if (status === STATUS.SUCCEEDED) {
    return children;
  }

  if (status === STATUS.FAILED) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="100vh"
        px={2}
      >
        <Stack spacing={2} maxWidth={480} width={1}>
          <Button
            variant="contained"
            onClick={() => {
              void dispatch(fetchTenantConfiguration());
            }}
          >
            {t("bootstrap.retry")}
          </Button>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      aria-busy
      aria-label={t("loading")}
    >
      <CircularProgress />
    </Box>
  );
}
